// Data hooks of the approval ("visa") screens (GED-FE-06): the generated reads and writes, and one
// refresh that brings every view of a space up to date after a start, a decision or a
// cancellation (flows, document details and locks, deliverables that follow the flow).
import {
  useAnnotationsControllerList,
  useApprovalTemplatesControllerCreate,
  useApprovalTemplatesControllerList,
  useApprovalTemplatesControllerRemove,
  useApprovalsControllerCancel,
  useApprovalsControllerDecide,
  useApprovalsControllerGet,
  useApprovalsControllerList,
  useApprovalsControllerListForDocument,
  useApprovalsControllerStart,
  usePartnerOfficesControllerList,
  type ApprovalsControllerListParams,
} from '@batiflow/api-client';
import { useQueryClient } from '@tanstack/react-query';

/** The flows of one document, newest first (the panel's section and badge). */
export function useDocumentFlows(spaceId: string, documentId: string | null) {
  return useApprovalsControllerListForDocument(spaceId, documentId ?? '', {
    query: { enabled: documentId !== null },
  });
}

/** The flows of a space (the project's Approvals tab), filtered by the server. */
export function useSpaceFlows(spaceId: string, params: ApprovalsControllerListParams) {
  return useApprovalsControllerList(spaceId, params);
}

/** One flow by its id (the Activity "Decide" action). */
export function useFlow(spaceId: string | null, flowId: string) {
  return useApprovalsControllerGet(spaceId ?? '', flowId, {
    query: { enabled: spaceId !== null },
  });
}

/** The annotations of a version: the reviewer links their own to a decision. */
export function useVersionAnnotations(spaceId: string, documentId: string, versionId: string) {
  return useAnnotationsControllerList(spaceId, documentId, versionId, { limit: 200 });
}

export const useStartApproval = useApprovalsControllerStart;
export const useDecideApproval = useApprovalsControllerDecide;
export const useCancelApproval = useApprovalsControllerCancel;

/** The approval templates of the organisation (every member reads them). */
export const useApprovalTemplates = useApprovalTemplatesControllerList;
export const useCreateApprovalTemplate = useApprovalTemplatesControllerCreate;
export const useDeleteApprovalTemplate = useApprovalTemplatesControllerRemove;

/** The partner offices a template step can ask (active partners of the shared spaces, COLX-BE-02). */
export const usePartnerOffices = usePartnerOfficesControllerList;

/**
 * Refreshes what an approval changes: the flows and documents of the space (a document is locked
 * while a flow runs), and the deliverables and phases of the projects (they follow the flow).
 */
export function useRefreshApprovals(spaceId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  const spacePrefix = `/api/v1/spaces/${spaceId}/`;
  return () =>
    queryClient.invalidateQueries({
      predicate: (query) => {
        const [key] = query.queryKey;
        if (typeof key !== 'string') return false;
        return (
          key.startsWith(`${spacePrefix}approval-flows`) ||
          key.startsWith(`${spacePrefix}documents`) ||
          key.includes('/deliverables') ||
          key.includes('/phases')
        );
      },
    });
}
